import { useEffect, useMemo, useState, type ElementType } from 'react';

import { Box, Divider, Flex, IconProps } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableProvidedDragHandleProps,
  DraggableStateSnapshot,
  Droppable,
  DroppableStateSnapshot,
  DropResult,
} from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { useGetNudgeCardsQuery } from 'screens/meshRecommends/dataHooks';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { OneOnOneAgenda } from 'screens/oneOnOneRabbit/interfaces/OneOnOneAgenda';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { check1on1TranscriptionEnabled, checkOneOnOneMLEnabled } from 'utils/helpers/featureFlags';
import { z } from 'zod';

import { OneOnOneIconDualTone, Plus, Reorder } from 'assets/assetsV1/iconComponents';

import { isMobileView } from 'constants/isMobileView';
import { sendAgendaItemsReorderedEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { CardTemplate } from '../../common/CardTemplate';
import SuggestionsTemplate from '../../common/SuggestionsTemplate';
import { oneOneOneReactQueryKeys } from '../../constants/oneOnOneReactQueryKeys';
import { useOneOnOneInstanceDetails } from '../../hooks/dataHooks';
import { setAgendaLibraryIsOpen } from '../../slice/OneOnOneInstancePageSlice';
import { useAgendaRecommendationsApiQuery } from '../AgendaLibrary/hooks/dataHooks';
import { OneOnOneAgendaLibraryI } from '../AgendaLibrary/interface';
import { addAgendaFromLibraryToInstance } from '../AgendaLibrary/services';
import MlSuggestions from '../Initiatives/components/MlSuggestions';
import { AgendaItem } from './components/AgendaItem';
import AgendaRecommendations from './components/AgendaRecommendations';
import { CreateAgenda, createAgendaObject } from './components/CreateAgenda';
import { NoDropLine } from './components/NoDropLine';
import {
  OneOnOneAgendaCreateOrUpdateI,
  useCreateAgendaMutation,
  useOneOnOneAgendaQuery,
  useOneOnOneBotRecommendedDataQuery,
  useReorderAgendaMutation,
} from './hooks/dataHooks';
import { deleteOneOnOneAiRecommendedData } from './services';

export const ReorderHandle = ({
  isDragging,
  index,
  ...props
}: DraggableProvidedDragHandleProps & {
  isDragging: boolean;
  index: number;
}) => {
  const ReorderIcon: ElementType<IconProps> = Reorder;
  return (
    <Box
      w="20px"
      h="20px"
      bg="#E6F1FD"
      borderRadius="50%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="absolute"
      top={isDragging && index === 0 ? '50px' : '26px'}
      left="-10px"
      className="reorder-handle"
      visibility={isDragging ? 'visible' : 'hidden'}
      {...props}
    >
      <ReorderIcon color="#1565BC" boxSize="20px" />
    </Box>
  );
};

interface DraggableAgendaProps {
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  parentSnapshot: DroppableStateSnapshot;
  agenda: z.infer<typeof OneOnOneAgenda>;
  setIsAgendaBeingDragged: (isDragging: boolean) => void;
  index: number;
}

const DraggableAgenda = ({
  provided,
  snapshot,
  parentSnapshot,
  agenda,
  setIsAgendaBeingDragged,
  index,
}: DraggableAgendaProps) => {
  useEffect(() => {
    setIsAgendaBeingDragged(snapshot?.isDragging);
  }, [snapshot?.isDragging, setIsAgendaBeingDragged]);

  return (
    <div {...provided.draggableProps} ref={provided.innerRef}>
      <Box
        sx={{
          '&:hover .reorder-handle': {
            visibility: 'visible',
          },
          pointerEvents: parentSnapshot.isDraggingOver ? 'none' : 'auto',
        }}
        position="relative"
        display="flex"
        w="100%"
        alignItems="center"
      >
        <ReorderHandle isDragging={snapshot.isDragging} index={index} {...provided.dragHandleProps} />
        <AgendaItem
          isDragging={snapshot.isDragging}
          isDraggingOver={parentSnapshot.isDraggingOver}
          key={agenda.instanceDataId}
          index={index}
          {...agenda}
        />
      </Box>
      <Divider opacity={1} color="#EBEFF3" />
    </div>
  );
};

const AgendaList = ({ agendaLibrary }: { agendaLibrary: OneOnOneAgendaLibraryI }) => {
  // REFER: https://github.com/atlassian/react-beautiful-dnd/issues/961#issuecomment-443379284

  const [creatingAgenda, setCreatingAgenda] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();

  const queryClient = useQueryClient();

  const { masterId, instanceId } = useOneOnOnePageParams();

  const [isAgendaBeingDragged, setIsAgendaBeingDragged] = useState(false);

  const [showList, setShowList] = useState(true);

  const { relationType, completionStatus } = useOneOnOneInstanceDetails({
    masterId,
    instanceId,
  });

  const { data: agendaItems, isLoading } = useOneOnOneAgendaQuery({
    masterId,
    instanceId,
  });

  const { data: agendaRecommendations } =
    useAgendaRecommendationsApiQuery({
      masterId,
      instanceId,
    }) ?? {};

  const adminAgendaItems = agendaItems?.filter((agenda) => agenda.isOwnerAdmin);

  const { data: rawNudgeCards, isLoading: isLoadingNudgeCards } = useGetNudgeCardsQuery({
    pageType: 'ONE_ON_ONE_PAGE',
  });

  const handleAddToAgenda = async (params: { id: number; agendaText: string }) => {
    const addedAgenda = await addAgendaFromLibraryToInstance({
      masterId,
      instanceId,
      agendaText: params.agendaText,
      libraryAgendaId: params.id,
    });

    if (addedAgenda) {
      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) => [...old, addedAgenda]
      );
      queryClient.invalidateQueries([REACT_QUERY_KEYS.meshRecommends.nudgeCards]);
      toast({
        title: 'Agenda has been added',
        status: 'success',
      });
    }
  };

  const { data } = useOneOnOneBotRecommendedDataQuery({
    masterId,
    instanceId,
  });

  const { aigeneratedAgendaItems } = data ?? [];

  const createAgendaMutation = useCreateAgendaMutation({
    masterId,
    instanceId,
  });

  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);

  // react beautiful dnd needs state to be updated in a sync manner that's why had to use useState instead of useMemo
  const [userCreatedAgendaItemsSorted, setUserCreatedAgendaItemsSorted] = useState<
    Array<z.infer<typeof OneOnOneAgenda>>
  >([]);

  useEffect(() => {
    if (!isLoading) {
      setUserCreatedAgendaItemsSorted(
        agendaItems?.filter((agenda) => !agenda.isOwnerAdmin).sort((a, b) => a.displayOrder - b.displayOrder)
      );
    }
  }, [agendaItems, isLoading]);

  // const userCreatedAgendaItemsSorted = useMemo(() => {
  //   return agendaItems
  //     ?.filter((agenda) => !agenda.isOwnerAdmin)
  //     .sort((a, b) => a.displayOrder - b.displayOrder);
  // }, [agendaItems]);

  const reorderAgendaMutation = useReorderAgendaMutation({
    masterId,
    instanceId,
  });

  const reorder = (list: Array<z.infer<typeof OneOnOneAgenda>>, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;
    if (!destination) {
      return;
    }
    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return;
    }

    let topInstanceDataId = null;
    const reorderInstanceDataId = userCreatedAgendaItemsSorted[source.index].instanceDataId;

    if (destination.index === 0) {
      topInstanceDataId = null;
    } else if (destination.index < source.index) {
      // when moving up
      topInstanceDataId = userCreatedAgendaItemsSorted[destination.index - 1].instanceDataId;
    } else if (destination.index > source.index) {
      // when moving down
      topInstanceDataId = userCreatedAgendaItemsSorted[destination.index].instanceDataId;
    } else {
      // when moving to the last
      topInstanceDataId = userCreatedAgendaItemsSorted[destination.index - 1].instanceDataId;
    }

    const reorderAgendaItems = reorder(userCreatedAgendaItemsSorted, source.index, destination.index);
    setUserCreatedAgendaItemsSorted(reorderAgendaItems);
    reorderAgendaMutation.mutate({
      reorderInstanceDataId,
      topInstanceDataId,
    });

    sendAgendaItemsReorderedEvent({
      eventProperties: {
        instance_id: instanceId,
      },
    });

    // console.log({ reorderAgendaItems, source, destination });
  };

  const addAgendaButtonOptions = [
    {
      value: 'Add Agenda Point',
      label: 'Add agenda point',
      icon: null,
      onClick: () => {
        setCreatingAgenda(true);
      },
    },
  ];

  if (relationType !== 'PEER') {
    addAgendaButtonOptions.push({
      value: 'Suggested Agenda Point',
      label: 'Suggested Agenda Point',
      icon: null,
      onClick: () => {
        dispatch(
          setAgendaLibraryIsOpen({
            isOpen: true,
          })
        );
      },
    });
  }

  const addMlAgenda = async (aiAgendaText) => {
    try {
      const newAgenda = createAgendaObject({
        agendaText: aiAgendaText?.generatedText,
        displayName: loggedInEmployeeData?.displayName,
        profilePhoto: loggedInEmployeeData?.profilePhotoSrc,
        userId: loggedInEmployeeData?.id,
      });
      createAgendaMutation.mutate({
        updatedAgenda: newAgenda,
        onMutation: () => setCreatingAgenda(false),
      });
    } catch (e) {
      console.error('Error creating agenda : ', e);
    }

    await deleteOneOnOneAiRecommendedData({
      masterId,
      instanceId,
      aiGeneratedDataId: aiAgendaText.aiGeneratedDataId,
    });
    queryClient.invalidateQueries([oneOneOneReactQueryKeys.botRecommendedData, masterId, instanceId]);
  };

  const showDivider = !(
    agendaRecommendations?.length > 0 ||
    (aigeneratedAgendaItems?.length > 0 && (completionStatus === 'COMPLETED' || completionStatus === 'CANCELLED'))
  );

  const showAiGeneratedAgendaItems =
    check1on1TranscriptionEnabled() &&
    (completionStatus === 'COMPLETED' || completionStatus === 'CANCELLED') &&
    aigeneratedAgendaItems?.length > 0;

  const showNudgeCards = rawNudgeCards?.length > 0 && completionStatus === 'YET_TO_START';

  const nudgeData = useMemo(() => {
    let applicableNudges = [];

    // Iterate over all elements in the rawNudgeCards array
    rawNudgeCards?.forEach((card) => {
      // Filter out the nudge cards that are not applicable to the current instance
      const filteredNudges = card?.data?.nudgeContent?.filter((val) => {
        const regex = /oneOnOne\/\d+\/(\d+)/;
        const instanceIdFromURL = val?.buttonLink?.match(regex)?.[1];
        return instanceIdFromURL === instanceId.toString();
      });

      // Add the filtered nudges to the applicableNudges array
      if (filteredNudges) {
        applicableNudges = applicableNudges.concat(filteredNudges);
      }
    });

    // Filter out the nudges that don't have a corresponding agenda text
    applicableNudges = applicableNudges?.filter((val) => {
      const agendaIdFromURL = val?.buttonLink?.split('agendaTextId=')[1];
      return agendaLibrary.find((agenda) => agenda.id === Number(agendaIdFromURL));
    });

    return applicableNudges;
  }, [agendaLibrary, instanceId, rawNudgeCards]);

  const atleastOneNudgeExists = useMemo(() => nudgeData?.length > 0, [nudgeData]);

  if (isLoading) {
    return null;
  }

  return (
    <CardTemplate Icon={OneOnOneIconDualTone} heading="Agenda" hasDivider={showDivider}>
      {checkOneOnOneMLEnabled() &&
        !isMobileView() &&
        completionStatus === 'YET_TO_START' &&
        !showNudgeCards &&
        agendaRecommendations?.length > 0 && <AgendaRecommendations agendaRecommendations={agendaRecommendations} />}
      {showAiGeneratedAgendaItems && (
        <SuggestionsTemplate
          title="Agenda points identified by Maven"
          subTitle="Additional agenda points discovered by Maven from your 1:1 call"
          showList={showList}
          setShowList={setShowList}
        >
          {aigeneratedAgendaItems?.map((val) => (
            <MlSuggestions
              title={val.generatedText}
              subTitle="Agenda"
              handleAdd={() => addMlAgenda(val)}
              key={val.id}
            />
          ))}
        </SuggestionsTemplate>
      )}
      {!isLoadingNudgeCards && showNudgeCards && atleastOneNudgeExists && (
        <SuggestionsTemplate
          title="Agenda points suggested by Maven"
          subTitle=""
          showList={showList}
          setShowList={setShowList}
        >
          {nudgeData?.map((val) => {
            const agendaIdFromURL = val?.buttonLink?.split('agendaTextId=')[1];
            const agendaText = agendaLibrary.filter((agenda) => agenda.id === Number(agendaIdFromURL))[0]?.agendaText;
            if (!agendaText) {
              return null;
            }
            return (
              <MlSuggestions
                title={agendaText}
                subTitle="Agenda"
                handleAdd={() =>
                  handleAddToAgenda({
                    id: Number(agendaIdFromURL),
                    agendaText,
                  })
                }
                key={val.nudgeId}
                isNudgeCard
              />
            );
          })}
        </SuggestionsTemplate>
      )}
      {adminAgendaItems?.map((agenda, index) => (
        <>
          <AgendaItem
            key={agenda.instanceDataId}
            {...agenda}
            isOwnerAdmin
            isDragging={false}
            isDraggingOver={false}
            index={index}
          />
          {index !== adminAgendaItems.length - 1 && <Divider opacity={1} color="#EBEFF3" />}
          {Boolean(index === adminAgendaItems.length - 1 && !isAgendaBeingDragged) && (
            <Divider opacity={1} color="#EBEFF3" />
          )}
        </>
      ))}
      {/* {!Boolean(isAgendaBeingDragged && adminAgendaItems?.length) && <Box h="40px" w="100%" />} */}
      {Boolean(isAgendaBeingDragged && adminAgendaItems?.length) && <NoDropLine />}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, parentSnapshot) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {userCreatedAgendaItemsSorted?.map((agenda, index) => (
                <Draggable key={index} draggableId={`draggable-${index}`} index={index}>
                  {(provided, snapshot) => (
                    <DraggableAgenda
                      provided={provided}
                      snapshot={snapshot}
                      parentSnapshot={parentSnapshot}
                      agenda={agenda}
                      setIsAgendaBeingDragged={setIsAgendaBeingDragged}
                      index={index}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {creatingAgenda && <CreateAgenda setCreatingAgenda={setCreatingAgenda} />}
      <Flex h="72px" alignItems="center">
        <Button
          ml="4px"
          StartIcon={Plus}
          variant="tertiary"
          onClick={() => {
            setCreatingAgenda(true);
          }}
          // isDisabled={creatingAgenda}
        >
          Add agenda point
        </Button>
        {relationType !== 'PEER' && !isMobileView() && <TextBodyMedium bold>Or</TextBodyMedium>}

        {relationType !== 'PEER' && !isMobileView() && (
          //  @ts-ignore
          <Button
            variant="tertiary"
            onClick={() => {
              dispatch(
                setAgendaLibraryIsOpen({
                  isOpen: true,
                })
              );
            }}
          >
            Add from library
          </Button>
        )}
      </Flex>
    </CardTemplate>
  );
};

export { AgendaList };
